import { Dialog, dialogContext, MessageDialog, useDialogManager } from '../dialog';
import { NotificationBanner } from '../notificationBanner';
import { removeNotification, showErrorNotification, showInfoNotification, showNotification } from '../notificationToaster';
import React, { FormEvent, FunctionComponent, Suspense, useContext, useEffect, useState } from 'react';
import { graphql } from 'react-relay';
import { useLazyLoadQuery, useMutation } from 'react-relay/hooks';
import config from 'src/shared/config';
import { stringFormat, T } from 'src/shared/helpers';
import { MarketPlaceProviders } from 'src/shared/hooks/__generated__/useSubscriptionData_subscriptionDataFragment.graphql';
import usePermissions from 'src/shared/hooks/usePermissions';
import { useSubscriptionData } from 'src/shared/hooks/useSubscriptionData';
import styled from 'styled-components';
import { upgradeDialogMutation } from './__generated__/upgradeDialogMutation.graphql';
import { upgradeDialogQuery, upgradeDialogQuery$data } from './__generated__/upgradeDialogQuery.graphql';

type DialogType = 'DIALOG' | 'BANNER';

const UpgradeDialogContainer = styled.div``;

const ContentDialogUpgrade = styled.p`
    font-size: 14px;
    line-height: 20px;
    margin-bottom: 20px;
`;

const NotificationBannerContainer = styled(NotificationBanner)`
    margin-bottom: 25px;
`;

const ContentNotificationBannerUpgrade = styled.p`
    margin: 5px 0;
    padding: 0;
`;

const QueryUpgradeDialog: FunctionComponent<{
    onLoaded: (data: upgradeDialogQuery$data) => void;
}> = ({ onLoaded }) => {
    const query = useLazyLoadQuery<upgradeDialogQuery>(
        graphql`
            query upgradeDialogQuery {
                planForUpgradeSeller {
                    planID
                    name
                    price
                    planPeriod
                    trialPeriod
                    version
                }

                subscription {
                    plan {
                        isCustom
                    }
                }

                ecommerceSettings {
                    ecommerceName
                }
            }
        `,
        {}
    );

    useEffect(() => {
        onLoaded(query);
    }, []);

    return null;
};

const _UpgradeDialog: FunctionComponent<{
    message: React.ReactElement<any> | string;
    className?: string;
    type?: DialogType;
    marketPlace?: MarketPlaceProviders;
    showCloseButton?: boolean;
    showUpgradeButtonAsLink?: boolean;
    buttonUpgradeText?: string;
    addonKeyToActivate?: string;
    addonNameToActivate?: string;
    redirectUrlFromDashboard?: string;
    onClose?: () => void;
    onUpgradeCompleted?: () => Promise<void>;
}> = ({
    message,
    className,
    type,
    marketPlace,
    showCloseButton,
    showUpgradeButtonAsLink,
    buttonUpgradeText,
    addonKeyToActivate,
    addonNameToActivate,
    redirectUrlFromDashboard,
    onClose,
    onUpgradeCompleted
}) => {
    const { showDialog, closeDialog } = useDialogManager();
    const { shopifyEcommerceSettings } = useSubscriptionData();
    const { isAllowed } = usePermissions();

    const { dialogId } = useContext(dialogContext);

    // State
    const [query, setQuery] = useState<upgradeDialogQuery$data | null>(null);
    const [isStartUpgrading, setIsStartUpgrading] = useState(false);
    const [isInternalStartUpgrading, setIsInternalStartUpgrading] = useState(false);

    // Mutations
    const [changePlan] = useMutation<upgradeDialogMutation>(graphql`
        mutation upgradeDialogMutation($planId: Long!) {
            changePlan(planId: $planId) {
                newPlanName
                errorType
                isSuccessful
            }
        }
    `);

    // UrlAction for form
    const urlAction = query?.ecommerceSettings?.ecommerceName === 'SHOPIFY' ? '/subscription/SubscribeShopify' : '/subscription/Seller/Subscription/ChangePlan';

    // Callbacks
    const onSubmitUpgradePlanForm = (e: FormEvent) => {
        e.preventDefault();

        if (query?.subscription?.plan?.isCustom ?? false) {
            closeDialog(dialogId);
            showErrorNotification({ message: T._('It seems you have a custom plan, please contact us at support@zakeke.com', 'Admin') });
            return;
        }

        if (addonKeyToActivate !== undefined && shopifyEcommerceSettings?.isTestStore === true && config.isProduction) {
            showDialog(
                'test-store',
                <MessageDialog
                    buttons={[
                        {
                            label: T._('OK', 'Admin'),
                            onClick: () => {
                                closeDialog('test-store');
                            }
                        }
                    ]}
                    showCloseButton={false}
                    message={
                        <>
                            {stringFormat(
                                T._("Sorry! Your account is currently a development store, which means you can't add '{0}' add-on at the moment.", 'Admin'),
                                addonNameToActivate ?? ''
                            )}
                            <br />
                            {T._('To access this feature, all you need to do is upgrade to an active Shopify plan.', 'Admin')}
                            <br />
                            <br />
                            {T._('Thanks for your understanding!', 'Admin')}
                        </>
                    }
                />
            );
            return;
        }

        showNotification({
            type: 'default',
            message: T._('Upgrading your plan...', 'Admin')
        });

        setIsStartUpgrading(true);
        const form = e.currentTarget as HTMLFormElement;
        form.submit();
    };

    const onUpgradePlan = () => {
        if (!isAllowed(['SUBSCRIPTION'])) {
            showErrorNotification({ message: T._('You do not have permission to upgrade the plan', 'Admin') });
            return;
        }

        if (query?.subscription?.plan?.isCustom ?? false) {
            closeDialog(dialogId);
            showInfoNotification({ message: T._('It seems you have a custom plan, please contact us at support@zakeke.com', 'Admin') });
            return;
        }

        if (addonKeyToActivate !== undefined && shopifyEcommerceSettings?.isTestStore === true && config.isProduction) {
            showDialog(
                'test-store',
                <MessageDialog
                    buttons={[
                        {
                            label: T._('OK', 'Admin'),
                            onClick: () => {
                                closeDialog('test-store');
                            }
                        }
                    ]}
                    showCloseButton={false}
                    message={
                        <>
                            {stringFormat(
                                T._("Sorry! Your account is currently a development store, which means you can't add '{0}' add-on at the moment.", 'Admin'),
                                addonNameToActivate ?? ''
                            )}
                            <br />
                            {T._('To access this feature, all you need to do is upgrade to an active Shopify plan.', 'Admin')}
                            <br />
                            <br />
                            {T._('Thanks for your understanding!', 'Admin')}
                        </>
                    }
                />
            );
            return;
        }

        setIsInternalStartUpgrading(true);
        const upgradeInProgressNotification = showNotification({
            type: 'default',
            message: T._('Upgrading your plan...', 'Admin')
        });

        changePlan({
            onCompleted: (response) => {
                setIsInternalStartUpgrading(false);
                removeNotification(upgradeInProgressNotification);
                closeDialog(dialogId);
                if (!response.changePlan.isSuccessful) {
                    showNotification({
                        type: 'danger',
                        message:
                            response.changePlan.errorType === 'GENERIC'
                                ? T._('A problem occurred while upgrading your plan', 'Admin')
                                : T._('A problem due to your payment method occurred while upgrading your plan', 'Admin'),
                        dismiss: {
                            duration: 5000
                        }
                    });
                } else {
                    showNotification({
                        type: 'success',
                        message: T._('Your plan has been upgraded successfully', 'Admin'),
                        dismiss: {
                            duration: 5000
                        }
                    });
                    if (onUpgradeCompleted) onUpgradeCompleted().then();
                }
            },
            variables: {
                planId: query?.planForUpgradeSeller!.planID ?? -1
            }
        });
    };

    // Component to Render
    const ComponentToRender =
        marketPlace === 'MICROSOFT_AZURE' ? (
            type === 'DIALOG' || !type ? (
                <Dialog
                    size='small'
                    title={T._("It's time to upgrade!", 'Admin')}
                    showCloseButton={showCloseButton}
                    buttons={[{ label: T._('OK', 'Admin'), outline: false, onClick: () => closeDialog(dialogId) }]}
                >
                    {message}
                </Dialog>
            ) : (
                <NotificationBannerContainer
                    type={'EXTRA'}
                    showCloseButton={false}
                    content={<ContentNotificationBannerUpgrade>{message}</ContentNotificationBannerUpgrade>}
                />
            )
        ) : type === 'DIALOG' || !type ? (
            <Dialog
                size='small'
                title={T._("It's time to upgrade!", 'Admin')}
                showCloseButton={showCloseButton}
                buttons={
                    query?.planForUpgradeSeller?.planID && query.planForUpgradeSeller?.version === 'VERSION_2'
                        ? config.isOnCustomplan
                            ? [{ label: T._('OK', 'Admin'), outline: false, onClick: () => closeDialog(dialogId) }]
                            : [
                                  {
                                      label: buttonUpgradeText ? buttonUpgradeText : T._('Upgrade plan', 'Admin'),
                                      disabled: isStartUpgrading || isInternalStartUpgrading,
                                      isSubmit: query.ecommerceSettings?.ecommerceName === 'SHOPIFY',
                                      onClick: query.ecommerceSettings?.ecommerceName !== 'SHOPIFY' ? onUpgradePlan : undefined
                                  }
                              ]
                        : config.isOnCustomplan
                          ? [{ label: T._('OK', 'Admin'), outline: false, onClick: () => closeDialog(dialogId) }]
                          : [
                                {
                                    label: buttonUpgradeText ? buttonUpgradeText : T._('Upgrade plan', 'Admin'),
                                    onClick: () => {
                                        window.open('/' + config.culture + '/Admin/Seller/Subscription', '_blank');
                                        closeDialog(dialogId);
                                    }
                                }
                            ]
                }
            >
                {typeof message === 'string' ? (
                    <ContentDialogUpgrade
                        dangerouslySetInnerHTML={{
                            __html: config.isOnCustomplan
                                ? T._(
                                      'It seems you have a custom plan, please contact us at support@zakeke.zendesk.com to ask for changes to your plan',
                                      'Admin'
                                  )
                                : message
                        }}
                    />
                ) : (
                    message
                )}
            </Dialog>
        ) : (
            <NotificationBannerContainer
                type={'EXTRA'}
                showCloseButton={showCloseButton}
                onClose={onClose}
                content={
                    typeof message === 'string' ? (
                        <ContentNotificationBannerUpgrade
                            dangerouslySetInnerHTML={{
                                __html: config.isOnCustomplan
                                    ? T._(
                                          'It seems you have a custom plan, please contact us at support@zakeke.zendesk.com to ask for changes to your plan',
                                          'Admin'
                                      )
                                    : message
                            }}
                        />
                    ) : (
                        message
                    )
                }
                buttons={
                    query?.planForUpgradeSeller?.planID && query.planForUpgradeSeller.version === 'VERSION_2'
                        ? config.isOnCustomplan
                            ? []
                            : [
                                  {
                                      linkButton: showUpgradeButtonAsLink ?? false,
                                      label: buttonUpgradeText ? buttonUpgradeText : T._('Upgrade plan', 'Admin'),
                                      disabled: isStartUpgrading || isInternalStartUpgrading,
                                      isSubmit: query.ecommerceSettings?.ecommerceName === 'SHOPIFY',
                                      onClick: query.ecommerceSettings?.ecommerceName !== 'SHOPIFY' ? onUpgradePlan : undefined
                                  }
                              ]
                        : config.isOnCustomplan
                          ? []
                          : [
                                {
                                    linkButton: showUpgradeButtonAsLink ?? false,
                                    label: buttonUpgradeText ? buttonUpgradeText : T._('Upgrade plan', 'Admin'),
                                    onClick: () => {
                                        window.open('/' + config.culture + '/Admin/Seller/Subscription', '_blank');
                                        closeDialog(dialogId);
                                    }
                                }
                            ]
                }
            />
        );

    // Query for componenent
    if (!query && marketPlace !== 'MICROSOFT_AZURE') {
        return (
            <UpgradeDialogContainer className={className}>
                {ComponentToRender}
                <Suspense fallback={null}>
                    <QueryUpgradeDialog onLoaded={setQuery}></QueryUpgradeDialog>
                </Suspense>
            </UpgradeDialogContainer>
        );
    }

    // Render
    return marketPlace === 'MICROSOFT_AZURE' ? (
        <UpgradeDialogContainer className={className}>{ComponentToRender}</UpgradeDialogContainer>
    ) : query ? (
        <UpgradeDialogContainer className={className}>
            {query.planForUpgradeSeller?.planID && query.planForUpgradeSeller?.version === 'VERSION_2' ? (
                <form method='POST' onSubmit={onSubmitUpgradePlanForm} action={urlAction} data-t1={'false'}>
                    <input name='__RequestVerificationToken' type='hidden' value={config.antiforgeryToken}></input>
                    <input type='hidden' name='planID' value={query.planForUpgradeSeller.planID} />
                    <input type='hidden' name='addonKeyToActivate' value={addonKeyToActivate ?? ''} />
                    <input type='hidden' name='redirectUrlFromDashboard' value={redirectUrlFromDashboard ?? ''} />
                    {ComponentToRender}
                </form>
            ) : (
                ComponentToRender
            )}
        </UpgradeDialogContainer>
    ) : null;
};

export const UpgradeDialog = styled(_UpgradeDialog)``;
export default UpgradeDialog;
